import {
  applicationStatusActiveList,
  checkCompanyDetailsComplete,
  checkContactDetailsComplete,
  DocumentStatusEnum,
  DocumentTypeEnum,
  EmployeePositionEnum,
  getApplicationStatusText,
  ProductTypeEnum,
} from '@utils/constants';
import moment from 'moment';
import config from '@app/configs/config';
import _ from 'lodash';
import { selectIsInitializing } from '@store/features/loading';
import { IRootState } from '@app/store';
import {
  IBankAccount,
  IDocument,
  IEmployee,
  ILatestProductStatusInfo,
  IUserData,
} from '@features/workspace/types';

export const selectPersonId = (state: IRootState) => state.workspace.personId;

export const selectUsers = (state: IRootState) =>
  _.sortBy(state.workspace.users, ['person.firstName', 'person.lastName']);

export const selectUsersData = (state: IRootState): IUserData[] => {
  const users = state.workspace.users;
  const employees = state.workspace.employees;
  const documents = state.workspace.documents;

  return users.map((user) => {
    const assignment =
      employees?.find((empl: IEmployee) => empl.userId === user.id) || null;
    const userDocuments =
      documents?.filter((doc: IDocument) => doc.userId === user.id) || [];

    return {
      ...user,
      position: assignment?.position ?? null,
      assignment,
      documents: userDocuments,
    };
  });
};

export const selectCompanies = (state: IRootState) => state.workspace.companies;

export const selectActiveCompanyId = (state: IRootState) =>
  state.workspace.activeCompanyId;

export const selectActiveCompany = (state: IRootState) =>
  state.workspace.companies?.find(
    (company) => company.id === state.workspace.activeCompanyId
  );

export const selectUsersDataFilterDirector = (state: IRootState) =>
  selectUsersData(state).filter(
    (userData) =>
      userData.assignment &&
      userData.assignment.position !== EmployeePositionEnum.AuthPerson
  );

export const selectUsersDataFilterVerifiedDirector = (state: IRootState) =>
  selectUsersDataFilterDirector(state).filter(
    (userData) =>
      userData?.person?.identityDocuments?.[0]?.isVerified &&
      userData?.address?.jumioIsVerified
  );

export const selectUsersDataFilterAuthPerson = (state: IRootState) =>
  selectUsersData(state).filter(
    (userData) =>
      userData.assignment &&
      userData.assignment.position === EmployeePositionEnum.AuthPerson
  );

export const selectUsersDataFilterVerifiedAuthPerson = (state: IRootState) =>
  selectUsersDataFilterAuthPerson(state).filter(
    (userData) =>
      userData?.person?.identityDocuments?.[0]?.isVerified &&
      userData?.address?.jumioIsVerified
  );

export const selectBankAccounts = (state: IRootState): IBankAccount[] =>
  state.workspace.bankAccounts;

export const selectFundingApplications = (state: IRootState) =>
  state.workspace.fundingApplications;

export const selectAdvances = (state: IRootState) => state.workspace.advances;

export const selectEmployees = (state: IRootState) => state.workspace.employees;

export const selectDocuments = (state: IRootState) => {
  const documents = state.workspace.documents.slice();

  documents.sort((docX, docY) => (docX.modifiedAt < docY.modifiedAt ? 1 : -1));

  return documents;
};

export const selectDocumentsFilterBusiness = (state: IRootState) =>
  selectDocuments(state).filter(
    (doc) =>
      ![
        DocumentTypeEnum.PersonalAddressRegistration,
        DocumentTypeEnum.ID,
        DocumentTypeEnum.TemporaryID,
        DocumentTypeEnum.SourceOfFunds,
        DocumentTypeEnum.SourceOfWealth,
      ].includes(doc.documentType) &&
      doc.zendeskStatus !== DocumentStatusEnum.Rejected
  );

export const selectDocumentsFilterDirector = (state: IRootState) =>
  selectDocuments(state).filter(
    (doc) =>
      selectUsersDataFilterDirector(state).some(
        (user: any) => user.id === doc.userId
      ) && doc.zendeskStatus !== DocumentStatusEnum.Rejected
  );

export const selectDocumentsFilterAuthPerson = (state: IRootState) =>
  selectDocuments(state).filter(
    (doc) =>
      selectUsersDataFilterAuthPerson(state).some(
        (user) => user.id === doc.userId
      ) && doc.zendeskStatus !== DocumentStatusEnum.Rejected
  );

export const selectDocumentsFilterRejected = (state: IRootState) =>
  selectDocuments(state).filter(
    (doc) => doc.zendeskStatus === DocumentStatusEnum.Rejected
  );

export const selectNotes = (state: IRootState) => state.workspace.notes;

export const selectPrimaryUser = (state: IRootState) => {
  const company = selectActiveCompany(state);
  const primaryUserId = company?.primaryUserId;
  const usersData = selectUsersData(state);

  return (
    (primaryUserId && usersData?.find((user) => user.id === primaryUserId)) ||
    usersData[0] ||
    null
  );
};

// Particular loading flags
export const selectIsBankAccountsLoading = (state: IRootState) =>
  state.workspace.isBankAccountsLoading;

export const selectIsCompanyDocumentsLoading = (state: IRootState) =>
  state.workspace.isCompanyDocumentsLoading;

export const selectIsDocumentsLoading = (state: IRootState) =>
  state.workspace.isDocumentsLoading;

export const selectIsUsersLoading = (state: IRootState) =>
  state.workspace.isUsersLoading;

export const selectIsEmployeesLoading = (state: IRootState) =>
  state.workspace.isEmployeesLoading;

export const selectIsCompanyDataLoading = (state: IRootState) =>
  selectIsInitializing(state) ||
  selectIsBankAccountsLoading(state) ||
  selectIsDocumentsLoading(state) ||
  selectIsUsersLoading(state) ||
  selectIsEmployeesLoading(state);

// Passed stages selection

export const selectHasCompanyVerified = (state: IRootState) => {
  const businessDocuments = selectDocumentsFilterBusiness(state);
  const usersData = selectUsersData(state);

  const hasBusinessAddressRegistrationVerified = businessDocuments.some(
    (doc: any) =>
      doc.documentType === DocumentTypeEnum.BusinessAddressRegistration &&
      [DocumentStatusEnum.Submitted, DocumentStatusEnum.Approved].includes(
        doc.zendeskStatus
      )
  );
  const hasBusinessRegistrationVerified = businessDocuments.some(
    (doc: any) =>
      doc.documentType === DocumentTypeEnum.BusinessRegistration &&
      [DocumentStatusEnum.Submitted, DocumentStatusEnum.Approved].includes(
        doc.zendeskStatus
      )
  );
  const hasContactsVerified = usersData.reduce((acc: boolean, user: any) => {
    acc = acc && user?.person?.identityDocuments?.[0]?.isVerified;
    acc =
      acc &&
      user?.documents?.some(
        (doc: any) =>
          doc.documentType === DocumentTypeEnum.PersonalAddressRegistration &&
          [DocumentStatusEnum.Submitted, DocumentStatusEnum.Approved].includes(
            doc.zendeskStatus
          )
      );

    return acc;
  }, true);

  return (
    hasBusinessAddressRegistrationVerified &&
    hasBusinessRegistrationVerified &&
    hasContactsVerified
  );
};

export const selectHasCompleteVerification = (state: IRootState) => {
  const fundingApplications = selectFundingApplications(state);
  const bankAccounts = selectBankAccounts(state);
  const hasCompanyVerified = selectHasCompanyVerified(state);

  return (
    hasCompanyVerified && fundingApplications?.length && bankAccounts?.length
  );
};

export const selectHasCompleteContactsDetails = (state: IRootState) => {
  const users = selectUsers(state);

  return users.reduce(
    (acc: boolean, user: any) => acc && checkContactDetailsComplete(user),
    true
  );
};

export const selectHasCompletePrimaryContactDetails = (state: IRootState) => {
  const primaryContact = selectPrimaryUser(state);

  return (
    primaryContact &&
    checkContactDetailsComplete(primaryContact) &&
    primaryContact?.assignment?.position !== undefined &&
    primaryContact?.assignment?.position !== EmployeePositionEnum.None
  );
};

export const selectHasCompleteProductSelection = (state: IRootState) => {
  const fundingApplications = selectFundingApplications(state);
  const bankAccounts = selectBankAccounts(state);

  return (
    !!bankAccounts?.length ||
    fundingApplications?.some(
      (funApp: any) => funApp.amount && funApp.fundingReason
    )
  );
};

export const selectHasCompleteCompanyDetails = (state: IRootState) => {
  const company = selectActiveCompany(state);

  return checkCompanyDetailsComplete(company);
};

export const selectLatestProductsStatusInfo = (
  state: IRootState
): ILatestProductStatusInfo[] => {
  const fundingApplications = selectFundingApplications(state);
  const bankAccounts = selectBankAccounts(state);

  const products: any = [];
  // Add all funding application products

  fundingApplications?.forEach((item: any) => {
    products.push({
      type: ProductTypeEnum.Funding,
      date: item.createdDate,
      item,
    });
  });
  // Add all banking products
  bankAccounts?.forEach((item: any) => {
    products.push({
      type: ProductTypeEnum.Banking,
      date: item.createdAt,
      item,
    });
  });

  // Sort by latest date
  products.sort((a: any, b: any) =>
    new Date(a.date) > new Date(b.date) ? -1 : 1
  );

  const latestFundingProduct = products.find(
    (p: any) => p.type === ProductTypeEnum.Funding
  );
  const latestBankingProduct = products.find(
    (p: any) => p.type === ProductTypeEnum.Banking
  );

  const bankingProductIsCompleted =
    bankAccounts?.find(
      (ba: { statusCode: string }) => ba.statusCode === '00'
    ) !== null; // Active statusCode length

  const result: ILatestProductStatusInfo[] = [];

  if (latestFundingProduct?.item) {
    result.push({
      isCompleted:
        latestFundingProduct.item.status in applicationStatusActiveList,
      startDate:
        (latestFundingProduct.date &&
          moment(latestFundingProduct.date).format(config.dateFormat)) ||
        null,
      statusText: getApplicationStatusText(latestFundingProduct.item),
      latestProduct: latestFundingProduct,
    });
  }
  if (latestBankingProduct?.item) {
    result.push({
      isCompleted: bankingProductIsCompleted,
      startDate:
        (latestBankingProduct.date &&
          moment(latestBankingProduct.date).format(config.dateFormat)) ||
        null,
      statusText: bankingProductIsCompleted ? 'Active' : 'Inactive',
      latestProduct: latestBankingProduct,
    });
  }

  return result;
};

export const selectActiveBankAccountNumber = (state: IRootState) => {
  return state.workspace.activeBankAccountNumber;
};

export const selectActiveBankAccount = (
  state: IRootState
): IBankAccount | null => {
  const activeBankAccountNumber = selectActiveBankAccountNumber(state);
  const bankAccounts = selectBankAccounts(state);

  return (
    bankAccounts.find((acc) => acc.accountNumber === activeBankAccountNumber) ??
    null
  );
};

export const selectIsTraderootDown = (state: IRootState): boolean => {
  return state.workspace.isTraderootDown;
};
